import React, { useState, useEffect } from 'react';
import { 
  Container, Typography, Box, Paper, Grid, Card, CardContent,
  CardActionArea, Button, CircularProgress, Alert, Divider
} from '@mui/material';
import { 
  Settings as SettingsIcon,
  MessageOutlined as MessageIcon,
  SecurityOutlined as SecurityIcon,
  VolumeOffOutlined as MuteIcon,
  FilterAltOutlined as FilterIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { chatsAPI, modulesAPI } from '../api/api';

const ModuleCard = ({ title, description, icon, color, onClick }) => (
  <Card sx={{ height: '100%' }}>
    <CardActionArea sx={{ height: '100%' }} onClick={onClick}>
      <CardContent>
        <Box sx={{ display: 'flex', mb: 2 }}>
          {icon}
          <Typography variant="h6" component="div" sx={{ ml: 1 }}>
            {title}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      </CardContent>
    </CardActionArea>
  </Card>
);

const ChatDetailsPage = () => {
  const { chatId } = useParams();
  const navigate = useNavigate();
  
  const [chat, setChat] = useState(null);
  const [modules, setModules] = useState([]);
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Загрузка данных о чате и модулях
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Загрузка списка модулей
        const modulesResponse = await modulesAPI.getModules();
        setModules(modulesResponse.data.modules || []);
        
        // Загрузка конфигурации чата
        try {
          const configResponse = await chatsAPI.getChatConfig(chatId);
          
          if (configResponse.data && configResponse.data.config) {
            setConfig(configResponse.data.config);
            console.log('Загружена конфигурация чата:', configResponse.data.config);
          } else {
            console.warn('API вернул пустую конфигурацию чата');
            setConfig({});  // Устанавливаем пустой объект вместо undefined
          }
        } catch (configError) {
          console.error('Ошибка загрузки конфигурации чата:', configError);
          setError('Не удалось загрузить конфигурацию чата. Проверьте права доступа.');
          setConfig({});  // Устанавливаем пустой объект в случае ошибки
        }
        
        // Загрузка информации о чате
        try {
          const chatsResponse = await chatsAPI.getChats();
          if (chatsResponse.data && Array.isArray(chatsResponse.data.chats)) {
            const currentChat = chatsResponse.data.chats.find(c => String(c.id) === String(chatId));
            if (currentChat) {
              setChat(currentChat);
              console.log('Найден чат:', currentChat);
            } else {
              console.warn(`Чат с ID ${chatId} не найден в списке доступных чатов`);
              setError('Чат не найден в списке доступных. Проверьте права доступа.');
            }
          } else {
            console.warn('API вернул некорректный список чатов');
            setError('Не удалось получить список чатов');
          }
        } catch (chatError) {
          console.error('Ошибка загрузки информации о чате:', chatError);
          setError('Не удалось загрузить информацию о чате');
        }
      } catch (err) {
        console.error('Общая ошибка загрузки данных:', err);
        setError('Не удалось загрузить данные о чате или модулях');
      } finally {
        setLoading(false);
      }
    };

    if (chatId) {
      fetchData();
    }
  }, [chatId]);

  // Навигация к странице настроек модуля
  const navigateToModule = (moduleName) => {
    navigate(`/chats/${chatId}/modules/${moduleName}`);
  };

  // Иконки модулей
  const getModuleIcon = (moduleName) => {
    switch (moduleName) {
      case 'system':
        return <SettingsIcon sx={{ color: 'primary.main' }} />;
      case 'grep':
        return <MessageIcon sx={{ color: 'success.main' }} />;
      case 'spam':
        return <SecurityIcon sx={{ color: 'error.main' }} />;
      case 'mute':
        return <MuteIcon sx={{ color: 'warning.main' }} />;
      case 'watchdog':
        return <FilterIcon sx={{ color: 'info.main' }} />;
      default:
        return <SettingsIcon />;
    }
  };

  // Получение статуса модуля (активен/неактивен)
  const getModuleStatus = (moduleName) => {
    try {
      // Проверяем, существует ли модуль в конфигурации
      if (!config || !config[moduleName]) {
        return 'Нет данных';
      }
      
      // Проверяем значение свойства enabled (некоторые модули могут использовать active)
      const isActive = config[moduleName].enabled === true || config[moduleName].active === true;
      return isActive ? 'Активен' : 'Неактивен';
    } catch (error) {
      console.error(`Ошибка получения статуса модуля ${moduleName}:`, error);
      return 'Нет данных';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!chat) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 4 }}>
          <Alert severity="error">
            Чат не найден или у вас нет прав доступа к нему
          </Alert>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/chats')}
            sx={{ mt: 2 }}
          >
            Вернуться к списку чатов
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/chats')}
          sx={{ mb: 2 }}
        >
          Назад к списку чатов
        </Button>
        
        <Typography variant="h4" component="h1" gutterBottom>
          {chat.title}
        </Typography>
        
        <Typography variant="body1" paragraph>
          ID: {chat.id} • Тип: {chat.type === 'supergroup' ? 'Супергруппа' : 'Группа'}
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Paper sx={{ p: 3, mt: 2 }}>
          <Typography variant="h5" gutterBottom>
            Модули бота
          </Typography>
          <Typography variant="body2" paragraph color="text.secondary">
            Выберите модуль для управления его настройками
          </Typography>
          
          <Divider sx={{ mb: 3 }} />
          
          {config && Array.isArray(modules) && modules.length > 0 ? (
            <Grid container spacing={3}>
              {modules.map((module) => (
                <Grid item xs={12} sm={6} md={4} key={module.name}>
                  <ModuleCard
                    title={module.title}
                    description={`Статус: ${getModuleStatus(module.name)}`}
                    icon={getModuleIcon(module.name)}
                    onClick={() => navigateToModule(module.name)}
                  />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Alert severity="info" sx={{ mt: 2 }}>
              {!modules || modules.length === 0 
                ? 'Не удалось загрузить список модулей.' 
                : 'Не удалось загрузить конфигурацию чата.'}
              <br />
              Попробуйте обновить страницу или вернуться к списку чатов.
            </Alert>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default ChatDetailsPage; 